import { ChevronRight } from '@mui/icons-material';
import { Box, Button, Checkbox, FormControlLabel, FormGroup, Stack, Typography } from '@mui/material';
import type React from 'react';
import { useNavigate } from 'react-router';
import ChipLabel from '/@/components/chip-label';
import type { UnitConverter } from '/@/core/units';
import { _ } from '/@/locale';
import { reverse } from '/@/utils/router';
import {
    getMaxAndMinEnvironmentFromRankings,
    getPositioningBucketsFromRankings,
    type PositioningBucket,
} from '../../../dashboard/rv/components/product-catalog';
import type { useProductComparator } from '../../hooks/product-comparator';

export type CornCycle = 'short' | 'intermediate' | 'long';
export const CornCycleChoices = {
    short: _('Corto'),
    intermediate: _('Intermedio'),
    long: _('Largo'),
};

export const parseProductForProductCard = (
    products: any[],
    units: UnitConverter,
    { largeChips = false }: { largeChips?: boolean } = { largeChips: false }
): {
    id: number;
    imageSrc: string;
    name: string;
    description: string;
    typeLabel: string;
    catalogType: 'corn' | 'soy' | 'crop_protection';
    slugLabel: string;

    catalogZoneId?: number;
    catalogName?: string;
    technologies?: {
        id: number;
        name: string;
        logoSrc: string;
    }[];
    newProduct?: boolean;
    cycle?: CornCycle;
    positioning?: {
        label: string;
    };
    positioningBuckets?: PositioningBucket[];

    chips: React.ReactNode[];

    type?: {
        id: number;
    };
    crops?: {
        id: number;
        name: {
            es: string;
            en: string;
            pt: string;
        };
    }[];
    rankings?: {
        minEnvironment: number;
        maxEnvironment: number;
    }[];
    newRankings?: {
        minEnvironment: number;
        maxEnvironment: number;
    }[];
}[] => {
    return products.map((p) => {
        const slugLabel = p.brand?.name?.toLowerCase() || p.brand?.toLowerCase() || '';
        if (p.catalogZone) {
            const positioning = getMaxAndMinEnvironmentFromRankings(p.rankings, p.newRankings, units);
            const positioningBuckets = getPositioningBucketsFromRankings(p.rankings, p.newRankings, units);
            return {
                ...p,
                id: p.id,
                imageSrc: p.brand.logo,
                name: p.name,
                description: _(p.shortDescription),
                typeLabel: _('Híbrido'),
                technologies: (p.technologies || []).map((t) => ({ id: t.id, name: t.name, logoSrc: t.logoImage })),
                newProduct: p.newProduct,
                chips: [
                    <ChipLabel
                        key={p.id + p.catalogZone}
                        label={`${largeChips ? `${_('Zona')} ` : ''}${_(p.catalogZone)}`}
                        type='catalogZone'
                    />,
                    ...(p.cycle
                        ? [
                              <ChipLabel
                                  key={p.id + CornCycleChoices[p.cycle as CornCycle]}
                                  label={`${largeChips ? `${_('Ciclo')} ` : ''} ${CornCycleChoices[p.cycle as CornCycle]}`}
                                  type='cycle'
                              />,
                          ]
                        : []),
                    ...(positioning !== undefined && positioning?.label !== '-'
                        ? [
                              <ChipLabel
                                  key={p.id + positioning.label}
                                  label={`${largeChips ? `${_('Posicionamiento')} ` : ''} ${positioning.label}`}
                                  type='positioning'
                              />,
                          ]
                        : []),
                ],
                catalogType: p.catalogType || p.brand.type,
                slugLabel,
                positioning,
                positioningBuckets,
            };
        }

        // CP
        return {
            ...p,
            id: p.id,
            imageSrc: p.image,
            name: p.name,
            description: _(p.shortDescription),
            typeLabel: _(p.type),
            chips: (p?.crops || []).map((c) => {
                return <ChipLabel key={c.id} label={_(c.name)} type='crop' />;
            }),
            catalogType: p.catalogType || 'crop_protection',
            slugLabel,
        };
    });
};

const ProductCard = ({
    id,
    imageSrc,
    name,
    description,
    typeLabel,
    chips,

    technologies,
    newProduct,

    catalogType,
    slugLabel,

    selectProduct,
    isSelected,

    disabled = false,
    rootId,
}: {
    id: number;
    imageSrc: string;
    name: string;
    description: string;
    typeLabel: string;
    chips?: React.ReactNode[];

    technologies?: { name: string }[];
    newProduct: boolean;
    catalogType: string;
    slugLabel: string;
    selectProduct: () => void;
    isSelected: boolean;
    selectedType?: ReturnType<typeof useProductComparator>['selectedType'];
    disabled?: boolean;
    /** Optional DOM id for guided tours (e.g. card-dk74). */
    rootId?: string;
}) => {
    const navigate = useNavigate();

    return (
        <Stack id={rootId} sx={{ border: (t) => `1px solid ${t.palette.divider}`, height: '100%' }}>
            {newProduct && (
                <Typography
                    color='primary'
                    variant='subtitle2'
                    fontWeight='700'
                    sx={{ background: (t) => t.palette.primary.onSurface, color: 'white !important' }}
                    py={1}
                    px={2}
                >
                    {_('Nuevo lanzamiento')}
                </Typography>
            )}
            <Stack p={{ xs: 2 }} spacing={2}>
                {catalogType === 'crop_protection' && (
                    <Box
                        component='img'
                        src={imageSrc}
                        alt='product-image'
                        mb={4}
                        height={'60px'}
                        sx={{
                            alignSelf: 'flex-start',
                        }}
                    />
                )}
                {catalogType !== 'crop_protection' && imageSrc && (
                    <Box
                        component='img'
                        src={imageSrc}
                        alt='brand-logo'
                        height={'28px'}
                        sx={{
                            alignSelf: 'flex-start',
                        }}
                    />
                )}
                <Stack spacing={0.5}>
                    <Typography color='primary' variant='h5'>
                        {name}
                    </Typography>
                    <Typography color='primary.mui.inactive' variant='body2'>
                        {typeLabel}
                    </Typography>
                </Stack>
                <Stack direction='row' spacing={1} mb={2} useFlexGap flexWrap='wrap'>
                    {chips || []}
                </Stack>
                <Typography color='primary' mb={'auto'} sx={{ overflow: 'auto' }}>
                    {description}
                </Typography>
                {(technologies || [])?.length > 0 && (
                    <Stack spacing={1} my={'16px !important'}>
                        <Typography color='primary' variant='caption' fontWeight='700'>
                            {_('Tecnologías disponibles')}
                        </Typography>

                        <Box component='ul' sx={{ ml: '16px !important' }}>
                            {technologies?.map(({ name }) => (
                                <li key={name}>
                                    <Typography color='primary' variant='caption'>
                                        {name}
                                    </Typography>
                                </li>
                            ))}
                        </Box>
                    </Stack>
                )}
            </Stack>

            <Stack
                direction={{ sm: 'row' }}
                justifyContent='space-between'
                alignItems='center'
                mt={'auto !important'}
                p={2}
                sx={{
                    borderTop: (t) => `1px solid ${t.palette.divider}`,
                }}
            >
                <Button
                    variant='text'
                    endIcon={<ChevronRight />}
                    onClick={() => {
                        navigate(
                            reverse('productExploration.detail', {
                                params: { type: catalogType, slug: slugLabel, productId: id },
                            })
                        );
                    }}
                >
                    {_('Ver detalle')}
                </Button>
                {catalogType !== 'crop_protection' && (
                    <FormGroup>
                        <FormControlLabel
                            control={
                                <Checkbox onChange={() => selectProduct()} checked={isSelected} disabled={disabled} />
                            }
                            label={
                                <Typography color='primary' variant='body2'>
                                    {_('Comparar')}
                                </Typography>
                            }
                            labelPlacement='start'
                            sx={{ px: 2, mx: 0 }}
                        />
                    </FormGroup>
                )}
            </Stack>
        </Stack>
    );
};

export default ProductCard;
